import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import VideoPlayer from './VideoPlayer';
import LiveChat from './LiveChat';
import Notes from './Notes';
import { useUI } from '../../../../contexts/UIContext';
import { 
  Card as BaseCard, 
  IconButton 
} from '../../../../styles/components';
import { glassDeep, glassMedium } from '../../../../styles/components/common/glassmorphism';
import { fadeInAnimation, scaleInAnimation } from '../../../../styles/components/common/animations';

const PanelOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.95);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  animation: ${fadeInAnimation} 0.3s ease-out;
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textOnDark};
  ${glassDeep}
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textOnDark};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const StatusBadge = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.success};
  background: rgba(16, 185, 129, 0.1);
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(16, 185, 129, 0.2);
  }
`;

const StatusIndicator = styled.span`
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.success};
  border-radius: 50%;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      opacity: 0.5;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
    100% {
      opacity: 0.5;
      transform: scale(0.8);
    }
  }
`;

const CloseButton = styled(IconButton)`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textOnDark};
  margin-left: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.textOnDark};
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(90deg);
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  height: calc(100vh - 64px);
  overflow: hidden;
  animation: ${scaleInAnimation} 0.3s ease-out;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  min-height: 0;
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  min-height: 0;
`;

const TabContainer = styled.div`
  display: flex;
  background: rgba(15, 23, 42, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  ${glassMedium}
`;

const Tab = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  background: ${props => props.active ? 'rgba(99, 102, 241, 0.2)' : 'transparent'};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${props => props.active ? props.theme.colors.textOnDark : 'rgba(255, 255, 255, 0.7)'};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ theme }) => theme.colors.textOnDark};
  }
`;

const ParticipantsList = styled(BaseCard)`
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textOnDark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  height: 100%;
  overflow: auto;
  ${glassDeep}
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const ParticipantCount = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textOnDark};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const ParticipantItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ParticipantAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primaryLight};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all 0.3s ease;
  
  ${ParticipantItem}:hover & {
    background: rgba(99, 102, 241, 0.3);
    transform: scale(1.05);
  }
`;

const LiveWorkshopPanel = ({ workshop, onClose }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [notes, setNotes] = useState(() => {
    return localStorage.getItem(`workshop-notes-${workshop.id}`) || '';
  });
  const { setWorkshopModalOpen } = useUI();

  useEffect(() => {
    // Set modal open when component mounts
    setWorkshopModalOpen(true);
    
    // Set modal closed when component unmounts
    return () => setWorkshopModalOpen(false);
  }, [setWorkshopModalOpen, workshop.id]);

  const handleSaveNotes = (newNotes) => {
    setNotes(newNotes);
    localStorage.setItem(`workshop-notes-${workshop.id}`, newNotes);
  };

  return (
    <PanelOverlay>
      <Header>
        <Title>
          {workshop.title}
          <StatusBadge>
            <StatusIndicator />
            Live Now
          </StatusBadge>
        </Title>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </Header>
      
      <Content>
        <MainSection>
          <VideoPlayer
            src={workshop.meetingLink}
            poster={workshop.thumbnailUrl}
            isLive={true}
            style={{
              width: '100%',
              aspectRatio: '16/9',
              backgroundColor: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          />
          <Notes
            workshopId={workshop.id}
            initialValue={notes}
            onSave={handleSaveNotes}
            style={{
              marginTop: '1rem',
              height: '200px',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
            }}
          />
        </MainSection>
        
        <SideSection>
          <TabContainer>
            <Tab
              active={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
            >
              Live Chat
            </Tab>
            <Tab
              active={activeTab === 'participants'}
              onClick={() => setActiveTab('participants')}
            >
              Participants ({workshop.currentAttendees})
            </Tab>
          </TabContainer>
          
          {activeTab === 'chat' ? (
            <LiveChat onlineCount={workshop.currentAttendees} />
          ) : (
            <ParticipantsList>
              <ParticipantCount>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                {workshop.currentAttendees} participants in session
              </ParticipantCount>
              {Array.from({ length: workshop.currentAttendees }).map((_, i) => (
                <ParticipantItem key={i}>
                  <ParticipantAvatar>
                    {String.fromCharCode(65 + i)}
                  </ParticipantAvatar>
                  <div>Participant {i + 1}</div>
                </ParticipantItem>
              ))}
            </ParticipantsList>
          )}
        </SideSection>
      </Content>
    </PanelOverlay>
  );
};

export default LiveWorkshopPanel;
